/* eslint-disable react/display-name */
import React, { useEffect, useState } from 'react'
import { Col, Row, Space, Image, message } from 'antd'
import { numberWithComma, roundNumber } from 'helpers/format'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import { TableListPage } from 'Components/common/Table'
import ConfirmWithChild from 'Components/modal/ConfirmWithChild'
import { GrnItemInfo } from 'Store/grn/types'
import { PurchaseProduct } from 'Store/purchase/types'
import { appConfig } from 'config'
import { ImageFallback } from 'helpers/constanst'
import { AppInput, AppTextArea } from 'Components/form'
import { cloneDeep, trim } from 'lodash'
import { CreditNoteItem } from 'Store/grn-payment/types'

type Props = {
  visible: boolean
  onCloseModal: () => void
  onConfirm?: (item: CreditNoteItem[], remark: string) => void
  products?: GrnItemInfo[]
  depy_amount?: number
}

const ReduceDept: React.FunctionComponent<Props> = props => {
  const { onCloseModal, visible, products = [], onConfirm = () => {}, depy_amount = 0 } = props
  const [productsData, setProductsData] = useState([] as GrnItemInfo[])
  const [remark, setRemark] = useState('')

  useEffect(() => {
    if (!visible) {
      setProductsData([])
      setRemark('')
    }
  }, [visible])

  useEffect(() => {
    if (visible) {
      setProductsData([...cloneDeep(products)])
    }
  }, [products, visible])

  const onSubmit = () => {
    const credit_item = [] as CreditNoteItem[]
    productsData.forEach(item => {
      if ((item.purchasing_item.credit_product_amount || 0) > 0) {
        credit_item.push({
          amount: item.purchasing_item.credit_product_amount || 0,
          grn_item_id: item.id || 0,
        })
      }
    })

    if (credit_item.length > 0 && remark !== '') {
      if (roundNumber(countCreditTotal()) > roundNumber(depy_amount)) {
        message.error(
          `ไม่สามารถลดหนี้ราคา ${roundNumber(
            countCreditTotal(),
          )} บาทได้ เนื่องจากคุณเหลือยอดค้างชำระเพียง ${numberWithComma(roundNumber(depy_amount))} บาท`,
        )
        return
      }
      onConfirm(credit_item, remark)
    } else {
      if (credit_item.length <= 0) {
        message.error('กรุณาระบุจำนวนลดหนี้อย่างน้อย 1 รายการ ')
        return
      }
      if (trim(remark) === '') {
        message.error('กรุณาระบุ เหตุผลในการขอลดหนี้/ส่งคืนสินค้า')
        return
      }
      if (roundNumber(countCreditTotal()) > roundNumber(depy_amount)) {
        message.error(
          `ไม่สามารถลดหนี้ราคา ${roundNumber(
            countCreditTotal(),
          )} บาทได้ เนื่องจากคุณเหลือยอดค้างชำระเพียง ${numberWithComma(roundNumber(depy_amount))} บาท`,
        )
        return
      }
    }
  }

  const renderInfo = (val: any) => {
    return <RowTile>{val}</RowTile>
  }

  const renderImage = (purchase_info: PurchaseProduct) => (
    <ThumbImage src={appConfig.assetCdnEndpoint + purchase_info.product_photo} fallback={ImageFallback} />
  )

  const renderProductName = (value: PurchaseProduct) => {
    const isSameTitle = value.product_name == value.product_option_name
    return (
      <>
        <RowTile>{value.product_name}</RowTile>
        {!isSameTitle && <ProductDesc>{value.product_option_name}</ProductDesc>}
      </>
    )
  }

  const onUpdatePrice = (product: GrnItemInfo, val: string) => {
    const newProduct =
      productsData &&
      productsData.map(item => {
        if (item.id === product.id) {
          let amount = parseInt(val)
          if (amount > product.received_amount) {
            amount = product.received_amount
          }
          if (!product.received_amount || product.received_amount <= 0) {
            amount = 0
          }
          item.purchasing_item.credit_product_amount = amount
        }
        return item
      })
    setProductsData([...newProduct])
  }

  const renderQuantity = (item: PurchaseProduct, record: GrnItemInfo) => {
    if (!record.received_amount || record.received_amount <= 0) {
      return ''
    }

    return (
      <AppInput
        name="credit_product_amount"
        label=""
        type="number"
        errorMsg="จำนวนลดหนี้"
        isRequired={true}
        isError={false}
        value={roundNumber(item.credit_product_amount)}
        onChange={val => {
          onUpdatePrice(record, val)
        }}
        key={`${record.id}`}
      />
    )
  }

  const renderPrice = (item: PurchaseProduct) => {
    return <TextRight>{numberWithComma(item.price_per_unit, true)}</TextRight>
  }

  const countCreditTotal = () => {
    let total = 0
    productsData.forEach(item => {
      total += item.purchasing_item.price_per_unit * (item.purchasing_item.credit_product_amount || 0)
    })
    return total
  }

  const columns = [
    {
      title: <ColTile>ลำดับ</ColTile>,
      dataIndex: 'no',
      key: 'no',
      render: (value: any, record: any, index: number) => {
        return renderInfo(index + 1)
      },
      width: '50px',
    },
    {
      title: <ColTile>รูปภาพ</ColTile>,
      dataIndex: 'purchasing_item',
      key: 'product_photo',
      render: renderImage,
      width: '100px',
    },
    {
      title: <ColTile>รหัสสินค้า</ColTile>,
      dataIndex: 'purchasing_item',
      key: 'product_sku',
      width: '100px',
      render: (value: PurchaseProduct) => renderInfo(value.product_sku || '-'),
    },
    {
      title: <ColTile>ยี่ห้อ</ColTile>,
      dataIndex: 'purchasing_item',
      key: 'product_manufacture',
      width: '100px',
      render: (value: PurchaseProduct) => renderInfo(value.product_manufacture || '-'),
    },
    {
      title: <ColTile>รายการ</ColTile>,
      dataIndex: 'purchasing_item',
      key: 'product_name',
      render: renderProductName,
      width: '120px',
    },
    {
      title: <ColTile>หน่วย</ColTile>,
      dataIndex: 'product_uom',
      key: 'product_uom',
      width: '100px',
    },
    {
      title: <ColTile>รับเข้า</ColTile>,
      dataIndex: 'original_received_amount',
      key: 'original_received_amount',
      width: '100px',
      render: (value: number) => renderInfo(numberWithComma(value || 0)),
    },
    {
      title: <ColTile>ลดหนี้</ColTile>,
      dataIndex: 'original_received_amount',
      key: 'original_received_amount',
      width: '100px',
      render: (val: number, record: GrnItemInfo) =>
        numberWithComma((record.original_received_amount || 0) - (record.received_amount || 0)),
    },
    {
      title: <ColTile>คงเหลือ</ColTile>,
      dataIndex: 'received_amount',
      key: 'received_amount',
      width: '100px',
      render: (value: number) => <RemainProduct>{renderInfo(numberWithComma(value || 0))}</RemainProduct>,
    },
    {
      title: (
        <ColTile>
          <TextRight>ราคา/หน่วย</TextRight>
        </ColTile>
      ),
      dataIndex: 'purchasing_item',
      key: 'price_per_unit',
      width: '100px',
      render: renderPrice,
    },
    {
      title: <ColTile>จำนวนลดหนี้ (หน่วย)</ColTile>,
      dataIndex: 'purchasing_item',
      key: 'received_amount',
      width: '120px',
      render: renderQuantity,
    },
  ]

  const renderConfirmInfo = () => {
    return (
      <ConfirmWrapper>
        <Row gutter={[8, 8]}>
          <Col span={24}>
            <Center>
              <Desc>กรุณาตรวจสอบความถูกต้องของข้อมูลก่อน &quot;ยืนยันลดหนี้&quot;</Desc>
            </Center>
          </Col>
          <Col span={24}>
            <TableListPage
              rowKey="id"
              tableName="product-purchase"
              columns={columns}
              datas={productsData}
              size={'small'}
              scrollX={800}
            />
          </Col>
          <Col span={24}>
            <Row justify="end" align="middle" gutter={[32, 8]}>
              <Col>
                <Space>
                  ยอดรวมลดหนี้ทั้งหมด <Price>{numberWithComma(countCreditTotal(), true)}</Price> บาท
                </Space>
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <AppTextArea
              label="เหตุผลในการขอลดหนี้/ส่งคืนสินค้า"
              isRequired={true}
              isError={false}
              name="remark"
              rows={2}
              value={remark}
              onChange={(val: string) => setRemark(val)}
            />
          </Col>
        </Row>
      </ConfirmWrapper>
    )
  }

  return (
    <ConfirmWithChild
      title={'กรอกข้อมูลรายการลดหนี้'}
      isShow={visible}
      handleOk={onSubmit}
      handleCancel={onCloseModal}
      child={renderConfirmInfo()}
      cancelBtnTxt="ปิด"
      confirmBtnTxt="ยืนยันลดหนี้"
      width={'80%'}
    />
  )
}

const Center = styled.div`
  text-align: center;
`

const Price = styled.div`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.title1};
`
const ConfirmWrapper = styled.div`
  padding: 10px;
  text-align: left;
`

const TextRight = styled.div`
  text-align: right;
  margin-right: 20px;
`

const Desc = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
`
const ThumbImage = styled(Image)`
  border-radius: 5px;
  width: 70px;
  height: 70px;
  object-fit: cover;
`

const ColTile = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
`

const RowTile = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
`

const ProductDesc = styled(RowTile)`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
`

const RemainProduct = styled.span`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
`

export default ReduceDept
